"use client";

import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import { Document, DocumentCategory, DocumentTypes, Vendor, VendorType, ComplianceStatus } from "@/prisma-package/src";
import {
  resolveUploadCategory,
  typesForCategory,
  categoryLabel,
} from "@/app/_lib/utils/document-category.utils";
import {
  detectionFieldsForSave,
  PendingDetection,
} from "@/app/_lib/utils/upload-detection.utils";
import { MAX_SEED_DOCS } from "@/app/_lib/utils/document-training-seed.utils";
import {
  DETECTION_STATUS_LABELS,
  DetectionStatus,
  describeDetectionStatus,
} from "@/app/_lib/utils/document-type-health.utils";
import useOrgUrl from "@/app/_lib/hooks/use-org-url";
import { getAllVendorsByOrganizationIdLazyAction } from "@/app/_lib/actions/vendor.actions";
import { createVerifiedDocumentsAction } from "@/app/_lib/actions/document.actions";
import { pickSpecFieldValues } from "@/app/_lib/utils/extraction-fields.utils";
import { useToast } from "@/app/_lib/hooks/use-toast";
import { useDocumentTypeDefinitions } from "@/app/_lib/hooks/use-document-type-definitions";
import { MdClose } from "react-icons/md";
import CloudImage from "@/app/_lib/components/cloudinary/cloud-image";
import { getEstDateTime } from "@/app/_lib/utils/format-date-time.utils";

type DocumentWithVendors = Document & {
  extractedVendors?: Array<{
    vendor: { id: string; name: string };
    confidence: number;
  }>;
};

/**
 * A file that has been uploaded to Cloudinary and OCR'd but NOT yet written to
 * the database. The verification modal presents these for review; the documents
 * are only created (via createVerifiedDocumentsAction) when the user confirms,
 * so cancelling discards them entirely.
 */
export type PendingUploadDocument = {
  publicId: string;
  secureUrl: string;
  width: number;
  height: number;
  documentText: string;
  detectedType: string;
  // Recognition verdict for the status pill; null when the uploader was
  // given an explicit type and never scored the document.
  detection: PendingDetection | null;
  uploadedAt: number;
  // The AI suggestion request runs per file after upload; "pending" until it
  // settles (success or failure), so the table can show a loading state.
  extractionStatus: "pending" | "done";
  // Populated asynchronously by the AI extraction step.
  suggestedDocumentId: string | null;
  suggestedVendor: { id: string; name: string; confidence: number } | null;
  // All company names the AI found in the document (primary first); persisted
  // on save so the doc view modal can offer corrections later.
  vendorCandidates: string[];
  // Other vendors those names matched, offered as one-click alternatives.
  suggestedAlternates: Array<{ id: string; name: string; confidence: number }>;
  // AI-extracted values for the detected type's custom extraction fields,
  // keyed by field key.
  suggestedFields: Record<string, string>;
};

type VerificationItem = {
  pending: PendingUploadDocument;
  // The (possibly user-corrected) document type; starts as the detected type.
  type: string;
  documentId: string | null;
  vendorId: string | null;
  // Custom field values (suggested, then user-edited), keyed by field key.
  // Kept whole across type changes — only the chosen type's keys render and
  // save, so switching away and back loses nothing.
  fieldValues: Record<string, string>;
};

const STATUS_BADGE_CLASSES: Record<DetectionStatus, string> = {
  recognized: "badge-success",
  close: "badge-warning",
  notRecognized: "badge-ghost",
};

// Lazy vendor type from the action
type LazyVendor = {
  id: string;
  name: string;
  ein: string;
  type: VendorType | null;
  complianceStatus: ComplianceStatus | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  organizations: { organizationId: string }[];
};

type Props = {
  pendingDocuments: PendingUploadDocument[];
  projectId: string;
  organizationId: string;
  vendors?: Vendor[] | LazyVendor[];
  // The tab (category) these documents were uploaded into. Used to warn when a
  // document looks like it belongs in a different tab.
  uploadCategory?: DocumentCategory;
  isOpen: boolean;
  isLoading?: boolean;
  loadingMessage?: string;
  onClose: () => void;
  onComplete: (createdDocuments: DocumentWithVendors[]) => void;
};

// Full-page takeover rather than a route: the pending documents only exist in
// the uploader's in-memory state (and are still being updated by in-flight
// extraction callbacks), so navigating away would unmount the uploader and
// discard them. This renders over the whole viewport instead.
const FullPageTakeover: React.FC<{
  title: string;
  content: React.ReactNode;
  buttons: React.ReactNode[];
  isOpen: boolean;
  onClose: () => void;
}> = ({ title, content, buttons, isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    // Lock the page behind the takeover so only its own content scrolls.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-base-100">
      <header className="flex items-center justify-between border-b border-base-300 px-6 py-4">
        <h1 className="text-xl font-bold">{title}</h1>
        <button
          type="button"
          className="btn btn-sm btn-ghost rounded-md px-1"
          onClick={onClose}
          aria-label="Close"
        >
          <MdClose size={20} />
        </button>
      </header>
      <main className="flex-1 overflow-y-auto px-6 py-4">{content}</main>
      {buttons.length > 0 && (
        <footer className="flex justify-end gap-2 border-t border-base-300 px-6 py-4">
          {buttons.map((button, index) => (
            <div key={index}>{button}</div>
          ))}
        </footer>
      )}
    </div>
  );
};

export const DocumentVerificationTable: React.FC<Props> = ({
  pendingDocuments,
  projectId,
  organizationId,
  vendors: providedVendors,
  uploadCategory,
  isOpen,
  isLoading: externalLoading = false,
  loadingMessage = "Loading...",
  onClose,
  onComplete,
}) => {
  const toast = useToast();
  const router = useRouter();
  const { toProjectPath } = useOrgUrl();
  const { definitions, labelFor } = useDocumentTypeDefinitions(projectId);

  // Checklist uploads are deliberate and never rerouted; everywhere else the
  // row's type is editable and routes the document to its own category.
  const typeIsEditable = uploadCategory !== DocumentCategory.CHECKLIST;

  // Types offered in the per-row select: everything that can land in either
  // the project-expense or vendor-compliance tab (OTHER deduped).
  const typeOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...typesForCategory(DocumentCategory.PROJECT_EXPENSE, definitions),
          ...typesForCategory(DocumentCategory.VENDOR_COMPLIANCE, definitions),
        ]),
      ),
    [definitions],
  );

  // Near-miss suggestion for a row detection left as OTHER: the best-scoring
  // candidate, when it is a real selectable type. Detection declined to claim
  // it (its score fell short of the type's calibrated threshold), so instead
  // of silently filing the document under Other, surface the near miss as a
  // one-click correction and let the user decide.
  const suggestionFor = (item: VerificationItem): string | null => {
    if (!typeIsEditable || item.type !== DocumentTypes.OTHER) return null;
    const bestType = item.pending.detection?.bestType;
    return bestType && typeOptions.includes(bestType) ? bestType : null;
  };

  // Where a row will be saved, given its current type.
  const destinationFor = (type: string): DocumentCategory | undefined =>
    resolveUploadCategory(type, uploadCategory, {
      hasProjectId: !!projectId,
      defs: definitions,
    });
  const [vendors, setVendors] = useState<(Vendor | LazyVendor)[]>([]);
  const [verificationItems, setVerificationItems] = useState<VerificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // After a save where some documents weren't recognized: the docs are saved
  // either way, and the modal stays open to offer creating a type from them.
  const [postSave, setPostSave] = useState<{
    created: DocumentWithVendors[];
    unrecognizedIds: string[];
  } | null>(null);

  useEffect(() => {
    if (!isOpen) setPostSave(null);
  }, [isOpen]);

  // Load the vendor list for the dropdown.
  useEffect(() => {
    const fetchVendors = async () => {
      if (!isOpen) return;

      if (providedVendors && providedVendors.length > 0) {
        setVendors(providedVendors);
        return;
      }

      setIsLoading(true);
      try {
        const [vendorError, vendorData] = await getAllVendorsByOrganizationIdLazyAction({ organizationId });
        if (!vendorError && vendorData) {
          setVendors((vendorData.vendors as LazyVendor[]) || []);
        }
      } catch (error) {
        console.error("Failed to fetch vendors:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVendors();
  }, [organizationId, isOpen, providedVendors]);

  // Keep the editable rows in sync with the pending uploads (which arrive
  // incrementally as files finish uploading and extracting). Merge rather than
  // rebuild: a row's user edits must survive another row's extraction settling.
  useEffect(() => {
    setVerificationItems((prev) => {
      const prevByPublicId = new Map(prev.map((item) => [item.pending.publicId, item]));
      return pendingDocuments.map((pending) => {
        const existing = prevByPublicId.get(pending.publicId);
        if (!existing) {
          return {
            pending,
            type: pending.detectedType,
            documentId: pending.suggestedDocumentId,
            vendorId: pending.suggestedVendor?.id ?? null,
            fieldValues: pending.suggestedFields,
          };
        }
        return {
          pending,
          type: existing.type,
          documentId: existing.documentId ?? pending.suggestedDocumentId,
          vendorId: existing.vendorId ?? pending.suggestedVendor?.id ?? null,
          // Suggestions settle under user edits: an edited key wins.
          fieldValues: { ...pending.suggestedFields, ...existing.fieldValues },
        };
      });
    });
  }, [pendingDocuments]);

  const suggestedCount = verificationItems.filter(
    (item) => suggestionFor(item) !== null,
  ).length;

  const applyAllSuggestions = () => {
    setVerificationItems((prev) =>
      prev.map((item) => {
        const suggested = suggestionFor(item);
        return suggested ? { ...item, type: suggested } : item;
      }),
    );
  };

  // Document ID / vendor suggestions are still being extracted for some rows;
  // saving now would persist documents without them.
  const isExtracting = verificationItems.some(
    (item) => item.pending.extractionStatus === "pending",
  );

  const handleTypeChange = (index: number, type: string) => {
    const newItems = [...verificationItems];
    newItems[index] = {
      ...newItems[index],
      type,
    };
    setVerificationItems(newItems);
  };

  const handleDocumentIdChange = (index: number, value: string) => {
    const newItems = [...verificationItems];
    newItems[index] = {
      ...newItems[index],
      documentId: value || null,
    };
    setVerificationItems(newItems);
  };

  const handleFieldValueChange = (index: number, key: string, value: string) => {
    setVerificationItems((prev) =>
      prev.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const fieldValues = { ...item.fieldValues };
        if (value) {
          fieldValues[key] = value;
        } else {
          delete fieldValues[key];
        }
        return { ...item, fieldValues };
      }),
    );
  };

  // The chosen type's custom extraction fields (what the row renders/saves).
  const fieldsForType = (type: string) =>
    definitions.find((definition) => definition.key === type)
      ?.extractionFields ?? [];

  const handleVendorChange = (index: number, vendorId: string | null) => {
    const newItems = [...verificationItems];
    newItems[index] = {
      ...newItems[index],
      vendorId,
    };
    setVerificationItems(newItems);
  };

  const handleSave = async () => {
    if (verificationItems.length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      const [createErr, createdDocuments] = await createVerifiedDocumentsAction({
        projectId,
        documents: verificationItems.map((item) => ({
          publicId: item.pending.publicId,
          secureUrl: item.pending.secureUrl,
          type: item.type,
          ...detectionFieldsForSave(
            item.pending.detection,
            item.pending.detectedType,
            item.type,
          ),
          category: destinationFor(item.type),
          width: item.pending.width,
          height: item.pending.height,
          documentText: item.pending.documentText,
          extractedDocumentId: item.documentId,
          vendorId: item.vendorId,
          vendorCandidates: item.pending.vendorCandidates,
          extractedData: pickSpecFieldValues(
            item.fieldValues,
            fieldsForType(item.type),
          ),
        })),
      });

      if (createErr || !createdDocuments) {
        throw new Error(createErr?.message ?? "Failed to save documents");
      }

      // Attach the chosen vendor to each created document so the list updates
      // immediately without a refetch.
      const withVendors: DocumentWithVendors[] = createdDocuments.map((doc) => {
        const item = verificationItems.find(
          (i) => i.pending.publicId === doc.publicId,
        );
        let extractedVendors: DocumentWithVendors["extractedVendors"] = [];
        if (item?.vendorId) {
          const vendor = vendors.find((v) => v.id === item.vendorId);
          if (vendor) {
            extractedVendors = [
              { vendor: { id: vendor.id, name: vendor.name }, confidence: 1.0 },
            ];
          }
        }
        return { ...doc, extractedVendors } as DocumentWithVendors;
      });

      // Mention documents that were routed to a different tab than the one
      // they were uploaded from, so the user knows where to find them.
      const routedCounts = new Map<DocumentCategory, number>();
      for (const item of verificationItems) {
        const destination = destinationFor(item.type);
        if (uploadCategory && destination && destination !== uploadCategory) {
          routedCounts.set(destination, (routedCounts.get(destination) ?? 0) + 1);
        }
      }
      const routedNote = Array.from(routedCounts.entries())
        .map(([cat, count]) => `${count} to ${categoryLabel(cat)}`)
        .join(", ");

      toast({
        message: `Saved ${withVendors.length} document${withVendors.length !== 1 ? "s" : ""}${routedNote ? ` (${routedNote})` : ""}`,
        type: "success",
        state: "active",
      });

      // Documents are saved regardless; when some weren't recognized (and the
      // user didn't assign them a type themselves), stay open and offer to
      // create a document type from them.
      const unrecognizedIds = withVendors
        .filter((doc) => {
          const item = verificationItems.find(
            (i) => i.pending.publicId === doc.publicId,
          );
          return (
            item?.pending.detection?.status === "notRecognized" &&
            item.type === DocumentTypes.OTHER
          );
        })
        .map((doc) => doc.id);

      if (unrecognizedIds.length === 0) {
        onComplete(withVendors);
      } else {
        setPostSave({ created: withVendors, unrecognizedIds });
      }
    } catch (error) {
      console.error("Failed to save documents:", error);
      toast({
        message: "Failed to save documents",
        type: "error",
        state: "active",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state for either external loading (files still uploading) or
  // internal vendor loading.
  if (externalLoading || isLoading) {
    return (
      <FullPageTakeover
        title="Verify Uploaded Documents"
        content={
          <div className="flex flex-col justify-center items-center h-full space-y-4">
            <span className="loading loading-spinner loading-lg"></span>
            <p className="text-base-content/70 text-center">
              {externalLoading ? loadingMessage : "Loading document data..."}
            </p>
          </div>
        }
        buttons={[]}
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  if (postSave) {
    const unrecognizedCount = postSave.unrecognizedIds.length;
    const finish = () => onComplete(postSave.created);
    const handleCreateType = () => {
      const ids = postSave.unrecognizedIds.slice(0, MAX_SEED_DOCS).join(",");
      onComplete(postSave.created);
      router.push(toProjectPath(projectId, `/documents/types/new?docs=${ids}`));
    };
    return (
      <FullPageTakeover
        title="Documents Saved"
        content={
          <div className="max-w-2xl mx-auto space-y-4">
            <div className="alert alert-success">
              <span>
                Saved {postSave.created.length} document
                {postSave.created.length !== 1 ? "s" : ""}.
              </span>
            </div>
            <p className="text-sm">
              {unrecognizedCount} of them{" "}
              {unrecognizedCount === 1 ? "wasn't" : "weren't"} recognized as
              any document type. You can teach the system by creating a new
              document type from {unrecognizedCount === 1 ? "it" : "them"}{" "}
              &mdash; no re-uploading needed.
            </p>
          </div>
        }
        buttons={[
          <button key="done" className="btn btn-outline" onClick={finish}>
            Done
          </button>,
          <button
            key="create-type"
            className="btn btn-primary"
            onClick={handleCreateType}
          >
            Create a document type from{" "}
            {unrecognizedCount === 1
              ? "this document"
              : `these ${unrecognizedCount} documents`}
          </button>,
        ]}
        isOpen={isOpen}
        onClose={finish}
      />
    );
  }

  return (
    <FullPageTakeover
      title="Verify Uploaded Documents"
      content={
        <div className="space-y-4">
          <p className="text-sm text-base-content/70">
            Review the extracted information below. Documents are only saved when
            you click Save &mdash; cancelling discards these uploads.
          </p>

          {suggestedCount > 1 && (
            <div className="alert alert-info py-2">
              <span className="text-sm">
                {suggestedCount} documents scored close to a known type, but
                not confidently enough to label automatically. Review each
                below, or apply the best guesses.
              </span>
              <button
                type="button"
                className="btn btn-sm"
                onClick={applyAllSuggestions}
              >
                Apply best guesses
              </button>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto overflow-y-visible">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th className="w-24">Preview</th>
                  <th className="min-w-[140px]">Type</th>
                  <th className="min-w-[200px]">Extracted Data</th>
                  <th className="min-w-[300px]">Vendor</th>
                  <th className="text-right">Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {verificationItems.map((item, index) => {
                  const destination = destinationFor(item.type);
                  const suggested = suggestionFor(item);
                  const isRouted =
                    !!uploadCategory &&
                    !!destination &&
                    destination !== uploadCategory;

                  return (
                    <tr key={item.pending.publicId} className="hover">
                      <td>
                        <div className="w-16 h-16 rounded overflow-hidden border">
                          <CloudImage
                            src={item.pending.publicId}
                            alt="Document preview"
                            width={64}
                            height={64}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="flex flex-col items-start gap-1">
                          {typeIsEditable ? (
                            <select
                              className="select select-bordered select-sm w-full"
                              value={item.type}
                              onChange={(e) => handleTypeChange(index, e.target.value)}
                            >
                              {typeOptions.map((type) => (
                                <option key={type} value={type}>
                                  {labelFor(type)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-sm">{labelFor(item.type)}</span>
                          )}
                          {/* A near miss and the status badge are the same
                              single detection result, so render exactly one
                              of them: the near miss reads as "best guess,
                              needs your confirmation", never as a second
                              verdict contradicting "Not recognized". */}
                          {suggested ? (
                            <div
                              className="text-xs text-base-content/70"
                              title={`This document scored like ${labelFor(suggested)}, but below the confidence level where types are applied automatically. Labeling it also teaches detection for future uploads.`}
                            >
                              Best guess: {labelFor(suggested)} &mdash; not
                              confident enough to label automatically.{" "}
                              <button
                                type="button"
                                className="btn btn-link btn-xs h-auto min-h-0 p-0 align-baseline whitespace-nowrap"
                                onClick={() =>
                                  handleTypeChange(index, suggested)
                                }
                              >
                                Label as {labelFor(suggested)}
                              </button>
                            </div>
                          ) : (
                            item.pending.detection && (
                              <span
                                className={`badge badge-sm whitespace-nowrap ${STATUS_BADGE_CLASSES[item.pending.detection.status]}`}
                                title={
                                  describeDetectionStatus(
                                    item.pending.detection.status,
                                  ) ??
                                  DETECTION_STATUS_LABELS[
                                    item.pending.detection.status
                                  ]
                                }
                              >
                                {
                                  DETECTION_STATUS_LABELS[
                                    item.pending.detection.status
                                  ]
                                }
                              </span>
                            )
                          )}
                          {destination && (
                            <span
                              className={`text-xs whitespace-nowrap ${
                                isRouted
                                  ? "text-warning"
                                  : "text-base-content/60"
                              }`}
                            >
                              Saves to {categoryLabel(destination)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="space-y-1">
                          <input
                            type="text"
                            className="input input-bordered input-sm w-full"
                            placeholder="Enter document ID"
                            value={item.documentId || ""}
                            onChange={(e) => handleDocumentIdChange(index, e.target.value)}
                          />
                          {item.pending.extractionStatus === "pending" && (
                            <div className="flex items-center gap-1 text-xs text-base-content/60">
                              <span className="loading loading-spinner loading-xs"></span>
                              Extracting document ID...
                            </div>
                          )}
                          {item.pending.extractionStatus === "done" &&
                            !item.pending.suggestedDocumentId &&
                            !item.documentId && (
                              <div className="text-xs text-base-content/60">
                                No document ID found
                              </div>
                            )}
                          {fieldsForType(item.type).map((field) => {
                            const value = item.fieldValues[field.key] ?? "";
                            const missingRequired =
                              !!field.required &&
                              !value &&
                              item.pending.extractionStatus === "done";
                            return (
                              <label
                                key={field.key}
                                className="flex flex-col gap-0.5"
                              >
                                <span className="text-xs text-base-content/60">
                                  {field.label}
                                  {field.required && " *"}
                                </span>
                                <input
                                  type="text"
                                  className={`input input-bordered input-sm w-full ${
                                    missingRequired ? "input-warning" : ""
                                  }`}
                                  inputMode={
                                    field.dataType === "number" ||
                                    field.dataType === "currency"
                                      ? "decimal"
                                      : undefined
                                  }
                                  placeholder={
                                    item.pending.extractionStatus === "pending"
                                      ? "Extracting..."
                                      : "Not found"
                                  }
                                  value={value}
                                  onChange={(e) =>
                                    handleFieldValueChange(
                                      index,
                                      field.key,
                                      e.target.value,
                                    )
                                  }
                                />
                              </label>
                            );
                          })}
                        </div>
                      </td>
                      <td>
                        <div className="space-y-1">
                          <select
                            className="select select-bordered select-sm w-full"
                            value={item.vendorId || ""}
                            onChange={(e) => handleVendorChange(index, e.target.value || null)}
                            disabled={item.pending.extractionStatus === "pending"}
                          >
                            <option value="">Select a vendor...</option>
                            {vendors
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((vendor) => (
                                <option key={vendor.id} value={vendor.id}>
                                  {vendor.name}
                                </option>
                              ))}
                          </select>
                          {item.pending.extractionStatus === "pending" && (
                            <div className="flex items-center gap-1 text-xs text-base-content/60">
                              <span className="loading loading-spinner loading-xs"></span>
                              Identifying vendor...
                            </div>
                          )}
                          {item.pending.suggestedVendor && (
                            <div className="text-xs text-base-content/60">
                              Auto-detected: {item.pending.suggestedVendor.name} (
                              {Math.round(item.pending.suggestedVendor.confidence * 100)}%)
                            </div>
                          )}
                          {item.pending.suggestedAlternates.filter(
                            (alt) => alt.id !== item.vendorId,
                          ).length > 0 && (
                            <div className="flex flex-wrap items-center gap-1 text-xs text-base-content/60">
                              <span>Also found:</span>
                              {item.pending.suggestedAlternates
                                .filter((alt) => alt.id !== item.vendorId)
                                .map((alt) => (
                                  <button
                                    key={alt.id}
                                    type="button"
                                    className="btn btn-xs btn-outline gap-1"
                                    title={`Assign ${alt.name} to this document`}
                                    onClick={() => handleVendorChange(index, alt.id)}
                                  >
                                    {alt.name}
                                    <span className="text-primary">&middot; Assign</span>
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="text-right text-xs text-base-content/60 whitespace-nowrap">
                        {getEstDateTime(new Date(item.pending.uploadedAt))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="text-sm text-base-content/70">
            {verificationItems.length} document{verificationItems.length !== 1 ? "s" : ""} ready to
            save
          </div>
        </div>
      }
      buttons={[
        <button
          key="cancel"
          className="btn btn-outline"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </button>,
        <button
          key="save"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSaving || isExtracting || verificationItems.length === 0}
        >
          {isSaving ? (
            <>
              <span className="loading loading-spinner loading-sm"></span>
              Saving...
            </>
          ) : isExtracting ? (
            <>
              <span className="loading loading-spinner loading-sm"></span>
              Extracting...
            </>
          ) : (
            "Save Documents"
          )}
        </button>,
      ]}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};
